import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getDashboardStats, getDeals } from '../utils/api';

// ── palette (matches global CSS design tokens) ───────────────
const MEMBER_COLORS = ['#6aad7b','#6b8aad','#a08bb8','#c4a04e','#c45c5c','#8badc4','#7c8590'];
const TYPE_COLORS   = { calls: '#6aad7b', emails: '#6b8aad', meetings: '#a08bb8' };

const PERIODS  = ['week','month','qtr','year'];
const METRIC_LABELS = { touched:'Touched', added:'Added', overdue:'Overdue', stale:'Going Cold' };
const PERIOD_LABELS = { week:'Week', month:'Month', qtr:'Quarter', year:'Year' };

function formatMoney(v) {
  if (!v && v !== 0) return '$0';
  const n = parseFloat(v);
  if (isNaN(n)) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function relDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const diff = Date.now() - d;
  if (diff < 86400000)  return d.toLocaleTimeString([], { hour:'numeric', minute:'2-digit' });
  if (diff < 604800000) return d.toLocaleDateString([], { weekday:'short' });
  return d.toLocaleDateString([], { month:'short', day:'numeric' });
}

function daysUntil(dateStr) {
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.round((new Date(dateStr + 'T00:00:00') - today) / 86400000);
}

function formatBucket(str, period) {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  if (period === 'week' || period === 'month') {
    return d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
  }
  return d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
}

// Custom tooltip shared by both charts
function ChartTooltip({ active, payload, label, period }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: 'var(--color-surface)', border: '1px solid var(--color-border)',
      borderRadius: 6, padding: '8px 12px', fontSize: '0.78rem',
    }}>
      <div style={{ color: 'var(--color-text-muted)', marginBottom: 4 }}>
        {formatBucket(label, period)}
      </div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>{p.name}</span>
          <span style={{ marginLeft: 'auto' }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Metric card ───────────────────────────────────────────────
function MetricCard({ label, value, sub, active, danger, warn, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--color-surface)',
        border: `${active ? '2px' : '1px'} solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 10, padding: '14px 16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
      }}
    >
      <div style={{ fontSize:'0.7rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--color-text-muted)', marginBottom:6 }}>
        {label}
      </div>
      <div style={{
        fontSize: '1.7rem', fontWeight: 700,
        color: danger ? 'var(--color-danger)' : warn ? 'var(--color-warning)' : 'var(--color-text-primary)',
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize:'0.72rem', color:'var(--color-text-muted)', marginTop:3 }}>{sub}</div>}
    </div>
  );
}

// ── Follow-up row ─────────────────────────────────────────────
function FollowUpRow({ c, overdue }) {
  const d = daysUntil(c.next_follow_up);
  return (
    <Link to={`/contacts/${c.id}`} className="follow-up-row">
      <div className={`contact-avatar avatar-${c.category}`} style={{ width:30, height:30, fontSize:'0.7rem', flexShrink:0 }}>
        {c.first_name?.[0]}{c.last_name?.[0]}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:500, fontSize:'0.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {c.first_name} {c.last_name}
        </div>
        <div style={{ fontSize:'0.75rem', color:'var(--color-text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {c.organization}
        </div>
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontSize:'0.75rem', fontWeight:600, color: overdue ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
          {overdue ? `${Math.abs(d)}d overdue` : d === 0 ? 'Today' : `in ${d}d`}
        </div>
        <div style={{ fontSize:'0.7rem', color:'var(--color-text-muted)' }}>
          {new Date(c.next_follow_up + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' })}
        </div>
      </div>
    </Link>
  );
}

// ── Cold row ──────────────────────────────────────────────────
function ColdRow({ c }) {
  const staleDays = c.last_interaction_date
    ? Math.round((Date.now() - new Date(c.last_interaction_date)) / 86400000)
    : null;
  return (
    <Link to={`/contacts/${c.id}`} className="follow-up-row">
      <div className={`contact-avatar avatar-${c.category}`} style={{ width:30, height:30, fontSize:'0.7rem', flexShrink:0 }}>
        {c.first_name?.[0]}{c.last_name?.[0]}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:500, fontSize:'0.85rem' }}>{c.first_name} {c.last_name}</div>
        <div style={{ fontSize:'0.75rem', color:'var(--color-text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {c.organization}
        </div>
      </div>
      <div style={{ fontSize:'0.75rem', fontWeight:600, color: staleDays === null ? 'var(--color-danger)' : 'var(--color-warning)', flexShrink:0 }}>
        {staleDays === null ? 'Never contacted' : `${staleDays}d ago`}
      </div>
    </Link>
  );
}

const COLD_THRESHOLDS = [14, 30, 60, 90];
const DEAL_STATUS_META = {
  active:         { label:'Active',         color:'var(--color-success)' },
  under_contract: { label:'Under Contract', color:'var(--color-warning)' },
  closed:         { label:'Closed',         color:'var(--color-text-secondary)' },
  dead:           { label:'Dead',           color:'var(--color-danger)' },
};
const INT_COLORS = { call:'var(--av-partner)', email:'var(--av-lender)', meeting:'var(--av-lp)' };

// ═════════════════════════════════════════════════════════════
export default function DashboardPage() {
  const [data,       setData]       = useState(null);
  const [dealData,   setDealData]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [period,     setPeriod]     = useState('month');
  const [metric,     setMetric]     = useState('touched');
  const [activeCard, setActiveCard] = useState('touched');
  const [cold,       setCold]       = useState(30);
  const [chartLoading, setChartLoading] = useState(false);

  // Initial load
  useEffect(() => {
    Promise.all([
      getDashboardStats({ period, metric }),
      getDeals().catch(() => null),
    ]).then(([dash, deals]) => {
      setData(dash);
      setDealData(deals);
    }).catch(console.error).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Reload chart data when period/metric changes
  const reloadChart = useCallback(async (newPeriod, newMetric) => {
    setChartLoading(true);
    try {
      const dash = await getDashboardStats({ period: newPeriod, metric: newMetric });
      setData(dash);
    } catch (e) { console.error(e); }
    finally { setChartLoading(false); }
  }, []);

  const handlePeriod = (p) => { setPeriod(p); reloadChart(p, metric); };
  const handleMetric = (m) => { setMetric(m); setActiveCard(m); reloadChart(period, m); };

  if (loading) return <div className="page-loading">Loading dashboard…</div>;
  if (!data)   return <div className="page-error">Failed to load dashboard.</div>;

  const {
    metrics, team_activity, chart_bar, chart_series, chart_buckets,
    chart_is_cumulative, overdue_follow_ups, upcoming_follow_ups,
    going_cold, recent_activity,
  } = data;

  const coldContacts = (going_cold || []).filter(c => {
    if (!c.last_interaction_date) return true;
    return (Date.now() - new Date(c.last_interaction_date)) / 86400000 >= cold;
  });

  const dealSummary    = dealData?.summary    || [];
  const dealsLeaderboard = dealData?.leaderboard || [];
  const getSummary     = (s) => dealSummary.find(r => r.status === s) || { count: 0, total_value: 0 };
  const activeSummary  = getSummary('active');

  // Build bar chart data — one row per member
  const barData = (chart_bar || []).filter(m => m.calls + m.emails + m.meetings > 0);

  // Build line chart data — one row per bucket
  const members = (chart_series || []);
  const lineData = (chart_buckets || []).map((bucket, i) => {
    const row = { bucket, label: formatBucket(bucket, period) };
    members.forEach(m => {
      row[m.name] = m.points[i]?.value || 0;
    });
    return row;
  });

  const toggleStyle = (active) => ({
    padding: '3px 10px', borderRadius: 4, border: '1px solid var(--color-border)',
    background: active ? 'var(--color-btn)' : 'transparent',
    color: active ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
    cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'var(--font-ui)',
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
      </div>

      {/* ── Metric cards (clickable → change chart metric) ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        <MetricCard
          label="Touched this week" value={metrics.touched_this_week}
          sub={`of ${metrics.total} total`}
          active={activeCard === 'touched'}
          onClick={() => handleMetric('touched')}
        />
        <MetricCard
          label="Added this week" value={metrics.added_this_week}
          active={activeCard === 'added'}
          onClick={() => handleMetric('added')}
        />
        <MetricCard
          label="Overdue follow-ups" value={metrics.overdue_follow_ups}
          danger={metrics.overdue_follow_ups > 0}
          active={activeCard === 'overdue'}
          onClick={() => handleMetric('overdue')}
        />
        <MetricCard
          label={`No contact ${cold}d+`} value={coldContacts.length}
          warn={coldContacts.length > 0}
          active={activeCard === 'stale'}
          onClick={() => handleMetric('stale')}
        />
      </div>

      {/* ── Chart section ─────────────────────────────────── */}
      <div className="card" style={{ padding:'16px 20px', marginBottom:20 }}>
        {/* Controls row — period toggle only; metric is driven by clicking the cards above */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, gap:8 }}>
          <div style={{ fontSize:'0.78rem', color:'var(--color-text-muted)' }}>
            Showing: <span style={{ color:'var(--color-text-primary)', fontWeight:500 }}>{METRIC_LABELS[metric]}</span>
            <span style={{ color:'var(--color-text-muted)', marginLeft:6, fontSize:'0.72rem' }}>(click a tile above to change)</span>
          </div>
          <div style={{ display:'flex', gap:4 }}>
            {PERIODS.map(p => (
              <button key={p} style={toggleStyle(period === p)} onClick={() => handlePeriod(p)}>
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>

        {chartLoading ? (
          <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--color-text-muted)', fontSize:'0.85rem' }}>
            Updating…
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            {/* Stacked bar — interactions per member by type */}
            <div>
              <div style={{ fontSize:'0.75rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--color-text-muted)', marginBottom:10 }}>
                Activity by team member
              </div>
              {barData.length === 0 ? (
                <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--color-text-muted)', fontSize:'0.82rem' }}>
                  No activity in this period
                </div>
              ) : (
                <ResponsiveContainer key={`bar-${period}-${metric}`} width="100%" height={180}>
                  <BarChart data={barData} layout="vertical" margin={{ left:0, right:16, top:4, bottom:4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize:11, fill:'var(--color-text-muted)' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize:12, fill:'var(--color-text-secondary)' }} axisLine={false} tickLine={false} width={48} />
                    <Tooltip content={<ChartTooltip period={period} />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:'0.72rem', paddingTop:6 }} />
                    <Bar dataKey="calls"    name="Calls"    stackId="a" fill={TYPE_COLORS.calls}    radius={[0,0,0,0]} />
                    <Bar dataKey="emails"   name="Emails"   stackId="a" fill={TYPE_COLORS.emails}   radius={[0,0,0,0]} />
                    <Bar dataKey="meetings" name="Meetings" stackId="a" fill={TYPE_COLORS.meetings} radius={[0,2,2,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Multi-line — time series per member */}
            <div>
              <div style={{ fontSize:'0.75rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--color-text-muted)', marginBottom:10 }}>
                {chart_is_cumulative ? 'Cumulative' : 'Point-in-time'} — {METRIC_LABELS[metric].toLowerCase()} over time
              </div>
              {members.length === 0 || lineData.every(r => members.every(m => !r[m.name])) ? (
                <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--color-text-muted)', fontSize:'0.82rem' }}>
                  No data in this period
                </div>
              ) : (
                <ResponsiveContainer key={`line-${period}-${metric}`} width="100%" height={180}>
                  <LineChart data={lineData} margin={{ left:0, right:16, top:4, bottom:4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="label" tick={{ fontSize:10, fill:'var(--color-text-muted)' }} axisLine={false} tickLine={false}
                      interval={Math.floor(lineData.length / 5)} />
                    <YAxis tick={{ fontSize:11, fill:'var(--color-text-muted)' }} axisLine={false} tickLine={false} width={24} />
                    <Tooltip content={<ChartTooltip period={period} />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:'0.72rem', paddingTop:6 }} />
                    {members.map((m, i) => (
                      <Line
                        key={m.id} type={chart_is_cumulative ? 'monotone' : 'linear'}
                        dataKey={m.name} name={m.name}
                        stroke={MEMBER_COLORS[i % MEMBER_COLORS.length]}
                        strokeWidth={2} dot={false} activeDot={{ r:4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Follow-ups + Going Cold ───────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div className="dashboard-panel">
          <div className="panel-header">
            <span>Follow-ups</span>
            {overdue_follow_ups.length > 0 && (
              <span style={{ color:'var(--color-danger)', fontSize:'0.72rem' }}>{overdue_follow_ups.length} overdue</span>
            )}
          </div>
          {overdue_follow_ups.length > 0 && (
            <>
              <div style={{ padding:'5px 12px 2px', fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', color:'var(--color-danger)', textTransform:'uppercase' }}>
                Overdue
              </div>
              {overdue_follow_ups.slice(0,5).map(c => <FollowUpRow key={c.id} c={c} overdue />)}
            </>
          )}
          {upcoming_follow_ups.length > 0 && (
            <>
              <div style={{ padding:'5px 12px 2px', fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', color:'var(--color-text-muted)', textTransform:'uppercase' }}>
                Coming up
              </div>
              {upcoming_follow_ups.slice(0,5).map(c => <FollowUpRow key={c.id} c={c} overdue={false} />)}
            </>
          )}
          {overdue_follow_ups.length === 0 && upcoming_follow_ups.length === 0 && (
            <div style={{ padding:'12px', fontSize:'0.82rem', color:'var(--color-text-muted)' }}>No upcoming follow-ups.</div>
          )}
        </div>

        <div className="dashboard-panel">
          <div className="panel-header">
            <span>Going Cold</span>
            <div style={{ display:'flex', gap:3 }}>
              {COLD_THRESHOLDS.map(t => (
                <button key={t} onClick={() => setCold(t)} style={toggleStyle(cold === t)}>{t}d</button>
              ))}
            </div>
          </div>
          {coldContacts.length === 0 ? (
            <div style={{ padding:'12px', fontSize:'0.82rem', color:'var(--color-text-muted)' }}>No contacts going cold at {cold}d.</div>
          ) : (
            coldContacts.slice(0,6).map(c => <ColdRow key={c.id} c={c} />)
          )}
        </div>
      </div>

      {/* ── Deal Pipeline + Leaderboard ───────────────────── */}
      {dealData && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          <div className="dashboard-panel">
            <div className="panel-header">
              <span>Deal Pipeline</span>
              <Link to="/deals" style={{ fontSize:'0.72rem', color:'var(--color-link)' }}>View all →</Link>
            </div>
            {['active','under_contract','closed','dead'].map(key => {
              const s = getSummary(key);
              if (!parseInt(s.count)) return null;
              return (
                <div key={key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', borderBottom:'1px solid var(--color-border-faint)' }}>
                  <span style={{ fontSize:'0.85rem', color: DEAL_STATUS_META[key].color }}>{DEAL_STATUS_META[key].label}</span>
                  <span style={{ fontSize:'0.82rem', color:'var(--color-text-secondary)' }}>
                    {s.count} deal{s.count !== 1 ? 's' : ''} · {formatMoney(s.total_value)}
                  </span>
                </div>
              );
            })}
            <div style={{ padding:'8px 12px', fontSize:'0.8rem', color:'var(--color-text-secondary)' }}>
              Active pipeline: <strong style={{ color:'var(--color-success)' }}>{formatMoney(activeSummary.total_value)}</strong>
            </div>
          </div>

          <div className="dashboard-panel">
            <div className="panel-header"><span>Closed Deals Leaderboard</span></div>
            {dealsLeaderboard.length === 0 ? (
              <div style={{ padding:'12px', fontSize:'0.82rem', color:'var(--color-text-muted)' }}>No closed deals yet.</div>
            ) : (
              dealsLeaderboard.map((m, i) => (
                <div key={m.id || m.display_name} className={`leaderboard-row ${i === 0 ? 'leaderboard-first' : ''}`}>
                  <span className="leaderboard-rank">#{i+1}</span>
                  <span className="leaderboard-name">{m.display_name}</span>
                  <span className="leaderboard-deals">{m.deal_count} deal{m.deal_count !== 1 ? 's' : ''}</span>
                  <span className="leaderboard-value">{formatMoney(m.total_value)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── Recent Activity Feed ──────────────────────────── */}
      <div className="dashboard-panel">
        <div className="panel-header"><span>Recent Activity</span></div>
        {!recent_activity?.length ? (
          <div style={{ padding:'16px 12px', fontSize:'0.82rem', color:'var(--color-text-muted)' }}>
            No interactions logged yet.
          </div>
        ) : (
          recent_activity.map(i => (
            <div key={i.id} className="activity-row">
              <div className={`activity-dot dot-${i.interaction_type}`} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:'0.82rem', marginBottom:2 }}>
                  <span style={{ color:'var(--color-text-secondary)', textTransform:'capitalize' }}>{i.interaction_type}</span>
                  {' · '}
                  <Link to={`/contacts/${i.contact_id}`} style={{ color:'var(--color-link)', fontWeight:500, textDecoration:'none' }}>
                    {i.contact_name}
                  </Link>
                  {i.contact_organization && (
                    <span style={{ color:'var(--color-text-muted)', fontSize:'0.75rem' }}> · {i.contact_organization}</span>
                  )}
                </div>
                <div style={{ fontSize:'0.75rem', color:'var(--color-text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {i.summary}
                </div>
                {i.deal_reference && (
                  <div style={{ fontSize:'0.7rem', color:'var(--color-text-muted)', marginTop:2 }}>Deal: {i.deal_reference}</div>
                )}
              </div>
              <div style={{ flexShrink:0, textAlign:'right', marginLeft:10 }}>
                <div style={{ fontSize:'0.75rem', color:'var(--color-text-secondary)' }}>{relDate(i.interaction_date)}</div>
                <div style={{ fontSize:'0.7rem', color:'var(--color-text-muted)', marginTop:2 }}>{i.logged_by_name}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
